import AppButton from 'features/common/AppButton';
import { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchFactCheck } from 'redux/article';
import { createBookmark, deleteBookmark } from 'redux/bookmark';
import { getEntities, getEntity } from 'redux/entity';
import styled from 'styled-components';
import { trackEvent } from 'utils/analytics';
import { ArticleContext } from 'utils/contexts/ArticleContext';
import { useQuery } from 'utils/hooks/useQuery';
import { useSelect } from 'utils/hooks/useSelect';
import { AITextGeneration, ButtonFactCheck, Fact, FactMatch } from 'utils/types';
import { v4 } from 'uuid';
import { ArticleGenerationFactModal } from './ArticleGenerationFactModal';
import { ArticleGenerationHighlightTag } from './ArticleGenerationHighlightTag';

type ReactHighlightWordsCustomHiglightTagProps = {
  children: React.ReactNode;
  style: React.CSSProperties;
  highlightIndex: number;
};

export const ArticleGenerationTextCard = memo<{
  generationId: string;
  suggestionIndex: number;
  content: string;
  keywords: string[];
  objectives: string[];
  objectives_en: string[];
  fact?: Fact;
  buttonFact?: ButtonFactCheck;
  generations: AITextGeneration;
}>(({ content, keywords, objectives, objectives_en, fact, suggestionIndex, generationId, generations, buttonFact }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const { t } = useTranslation(['article', 'common']);
  const userId = useSelect(({ me }) => me.user?.id);
  const { articleId } = useContext(ArticleContext);

  const article = useSelect(({ entity }) => getEntity(entity, 'article', articleId));
  const settings = useSelect(({ editor }) => editor.textGenerationSettings);
  const bookmarks = useSelect(({ entity }) => getEntities(entity, 'bookmark'));
  const { isLoading: isFactCheckLoading } = useQuery('fetch_fact_check' + generationId);

  const [isFactsModalOpen, setIsFactsModalOpen] = useState(false);
  const [activeObjectiveIndex, setActiveObjectiveIndex] = useState(0);
  const [activeObjective, setActiveObjective] = useState('');
  const [activeObjectivePosY, setActiveObjectivePosY] = useState(0);

  const bookmark = useMemo(() => {
    return bookmarks?.find((item) => item.type === 'text' && item.content === content);
  }, [bookmarks, content]);

  // @utils

  const matchedFacts: FactMatch[] = useMemo(() => {
    if (!fact) return [];
    return fact.match_list.map((matches, index) => ({
      content: fact.news_list?.[index]?.slice(matches.start, matches.end),
      objective_index: matches.objective_index,
      source_domain: fact.source_domain
    }));
  }, [fact]);

  const objectiveIndexesThatHaveFacts = useMemo(() => {
    return matchedFacts.map((item) => item.objective_index);
  }, [matchedFacts]);

  const getFactsForObjective = useCallback(
    (objectiveIndex: number) => {
      return matchedFacts.filter((item) => {
        return item.objective_index === objectiveIndex;
      });
    },
    [matchedFacts]
  );

  // @handlers

  const handleFactCheckClick = useCallback(() => {
    const sourceText = generations.generations[suggestionIndex].content;
    const { output_language: outputLanguage, fc_output_language: sameFactLanguage } = settings;
    dispatch(fetchFactCheck(articleId, generations, suggestionIndex, sameFactLanguage, outputLanguage));
    trackEvent({ event: 'Fact Check Requested', userId: userId!, properties: { sourceText } });
  }, [articleId, generations, settings, suggestionIndex, t, userId]);

  const handleCopyClick = useCallback(() => {
    navigator.clipboard.writeText(content);
    toast.success(t('copySuccessful'));
  }, [content, t]);

  const handleToggleBookmarkClick = useCallback(() => {
    if (bookmark) {
      dispatch(deleteBookmark(articleId, bookmark.id));
      toast.success(t('deleteBookmarkSuccessful'));
    } else {
      dispatch(createBookmark({ id: v4(), article_id: articleId, content, type: 'text' }));
      toast.success(t('bookmarkSuccessful'));
    }
  }, [content, article, bookmark]);

  const handleClickHighlightedText = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, objectiveIndex: number) => {
      const clickPosition = event.currentTarget.offsetTop;
      const contentHeight = containerRef.current?.parentElement?.offsetTop ?? 0;

      setActiveObjectiveIndex(objectiveIndex);
      setActiveObjective(objectives_en[objectiveIndex]);

      setActiveObjectivePosY(clickPosition + contentHeight);
      setIsFactsModalOpen(true);
    },
    [objectives_en]
  );

  // @render

  return (
    <Container ref={containerRef}>
      <Sentences
        searchWords={objectives}
        autoEscape={true}
        textToHighlight={content}
        highlightTag={({ children, highlightIndex, style }: ReactHighlightWordsCustomHiglightTagProps) => (
          <ArticleGenerationHighlightTag
            style={style}
            disabled={
              !!((buttonFact?.[objectives_en?.[highlightIndex] ?? ''] ?? [])?.length === 0) &&
              !objectiveIndexesThatHaveFacts.includes(highlightIndex)
            }
            onClick={(e) => handleClickHighlightedText(e, highlightIndex)}
            objectiveIndex={highlightIndex}
            generationId={generationId}
            suggestionIndex={suggestionIndex}
          >
            {children}
          </ArticleGenerationHighlightTag>
        )}
      />

      <ArticleGenerationFactModal
        isOpen={isFactsModalOpen}
        onClose={() => setIsFactsModalOpen(false)}
        objectivePositionY={activeObjectivePosY}
        facts={getFactsForObjective(activeObjectiveIndex)}
        buttonFact={buttonFact}
        generationId={generationId}
        suggestionIndex={suggestionIndex}
        objective={activeObjective}
        activeObjectiveIndex={activeObjectiveIndex}
      />

      {!!keywords.length && (
        <Keywords>
          {keywords.slice(0, 3).map((item, index) => (
            <Keyword key={`${item}-${index}`}>{item.slice(0, 30)}</Keyword>
          ))}
        </Keywords>
      )}

      <Actions>
        <Button text={t('copy')} onClick={handleCopyClick} />
        <Button text={Boolean(bookmark) ? t('removeFromBookmarks') : t('save')} onClick={handleToggleBookmarkClick} />
        <Button text="Fact Check" onClick={handleFactCheckClick} loading={isFactCheckLoading} />
      </Actions>
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  border-bottom: 1px solid #dce2f3;
  padding: 24px 28px;
  flex-direction: column;
`;

const Keywords = styled.div`
  display: flex;
  width: 100%;
  margin-top: 12px;
  & :not(:last-child) {
    margin-right: 8px;
  }
`;

const Keyword = styled.p`
  border: 1px solid #dce2f1;
  padding: 3px 8px;
  border-radius: 4px;
  background: rgba(237, 240, 249, 0.4);

  font-family: Nunito Sans;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  margin-top: 24px;
  & :not(:last-child) {
    margin-right: 8px;
  }
`;

const Button = styled(AppButton)`
  border: 1px solid rgba(69, 98, 171, 0.5);
  background-color: ${({ loading, theme }) => (loading ? theme.colors.dark[100] : '#edf0f9')};
  padding: 10px;
  border-radius: 4px;

  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: center;

  min-width: 104px;
  color: ${({ loading, theme }) => (loading ? theme.colors.gray[30] : '#2f3032')};

  &:hover {
    color: ${({ loading, theme }) => (loading ? theme.colors.gray[30] : 'white')};
    background-color: ${({ loading, theme }) => (loading ? theme.colors.dark[100] : theme.colors.primary[30])};
  }
`;

const Sentences = styled(Highlighter).attrs(({ theme }) => ({
  highlightStyle: { backgroundColor: theme.colors.highlight[40] }
}))`
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;
  color: #2d2e45;
  word-break: break-word;
  white-space: pre-wrap;
`;
